import apiClient from "./apiClient";
import { isOfflineMode } from "@/helpers/offlineMode";
import { MOCK_AGENTS, MOCK_DEPARTMENTS, MOCK_SEGMENTS, MOCK_TASK_QUEUES } from "@/data/insightsMockData";
import type {
  InsightsFilterOptions,
  InsightsQueryParams,
  InsightsQueryResponse,
  InsightsRecording,
} from "@/types/insightsApi";

const BASE = "/customercare/v1/call-insights";

const paramsSerializer = (params: Record<string, unknown>): string => {
  const sp = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (val === undefined || val === null) continue;
    if (Array.isArray(val)) {
      for (const v of val) sp.append(key, String(v));
    } else {
      sp.append(key, String(val));
    }
  }
  return sp.toString();
};

const getSegments = async (params: InsightsQueryParams, signal?: AbortSignal): Promise<InsightsQueryResponse> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 400));

    const filtered = MOCK_SEGMENTS.filter((seg) => {
      if (params.directions && !params.directions.includes(seg.direction)) return false;
      if (params.customerPhone && seg.customerPhone !== params.customerPhone) return false;
      if (params.queues && !params.queues.includes(seg.queue)) return false;
      if (params.departments && !params.departments.includes(seg.department)) return false;
      if (params.agents && !params.agents.includes(seg.agent)) return false;
      if (params.dateFrom && new Date(seg.date) < new Date(params.dateFrom)) return false;
      if (params.dateTo && new Date(seg.date) > new Date(params.dateTo)) return false;
      return true;
    });

    return filtered;
  }

  const response = await apiClient.get<InsightsQueryResponse>(`${BASE}/query`, {
    params,
    paramsSerializer: (p) => paramsSerializer(p as Record<string, unknown>),
    signal,
  });

  return response.data;
};

const getTaskRecordings = async (taskId: string, signal?: AbortSignal): Promise<InsightsRecording[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Vary mock count per task for realistic testing
    const count = (taskId.charCodeAt(3) % 6) + 1;
    return Array.from({ length: count }, (_, i) => ({
      sid: `RE${taskId.slice(0, 8)}_${i}`,
      accountSid: `AC${taskId.slice(2, 10)}`,
      conferenceSid: `CF${taskId.slice(2, 10)}`,
      callSid: `CA${taskId.slice(4, 12)}${i}`,
      duration: 20 + i * 15,
      startTime: new Date(Date.now() - (count - i) * 60000),
    }));
  }
  const response = await apiClient.get<InsightsRecording[]>(`${BASE}/tasks/${taskId}/recordings`, { signal });
  return response.data;
};

const getFilterOptions = async (signal?: AbortSignal): Promise<InsightsFilterOptions> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    return {
      queues: MOCK_TASK_QUEUES.map((q) => q.value),
      agents: MOCK_AGENTS.map((a) => a.value),
      departments: MOCK_DEPARTMENTS.map((d) => d.value),
    };
  }
  const [queuesRes, agentsRes, departmentsRes] = await Promise.all([
    apiClient.get<string[]>(`${BASE}/task-queues`, { signal }),
    apiClient.get<string[]>(`${BASE}/agents`, { signal }),
    apiClient.get<string[]>(`${BASE}/departments`, { signal }),
  ]);
  return {
    queues: queuesRes.data,
    agents: agentsRes.data,
    departments: departmentsRes.data,
  };
};

export const insightsService = {
  getSegments,
  getTaskRecordings,
  getFilterOptions,
};
